'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetTrades } from '@morfi/backend-sdk-v2/client';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { MARKET_TRADES_TAPE_FIELDS, VIEWER_TRADE_PROJECTION } from '@/lib/sdk/projections';
import { viewerMarketTradeQueryArgs } from '@/lib/sdk/viewer-market-trade-query';
type TradeQuery = ReturnType<typeof useGetTrades>;
type TradeRow = TradeQuery['rows'][number];
interface UseMarketTradeStreamsInput {
  readonly market: string;
  readonly tapeFrom: string;
  readonly viewerFrom: string;
  readonly viewerWallet?: string;
  readonly outcomeIndex?: string;
  readonly mints?: readonly string[];
  readonly subscribeTape?: boolean;
}
interface MarketTradeStreams {
  readonly tapeQuery: TradeQuery;
  readonly tapeRows: ReadonlyArray<TradeRow>;
  readonly viewerQuery: TradeQuery;
  readonly viewerRows: ReadonlyArray<TradeRow>;
}

/**
 * Canonical market-detail trade reads: one public live tape and one optional
 * viewer history. Consumers own only their chart-specific mapping and paging.
 */
export function useMarketTradeStreams(t0) {
  const $ = _c(11);
  const {
    market,
    tapeFrom,
    viewerFrom,
    viewerWallet,
    outcomeIndex,
    mints,
    subscribeTape: t1
  } = t0;
  const subscribeTape = t1 === undefined ? true : t1;
  const sharedScope = {
    market,
    ...(outcomeIndex !== undefined ? {
      outcomeIndex
    } : {}),
    ...(mints !== undefined ? {
      mints: [...mints]
    } : {})
  };
  const tapeQuery = useGetTrades({
    fields: [...MARKET_TRADES_TAPE_FIELDS],
    ...viewerMarketTradeQueryArgs({
      ...sharedScope,
      from: tapeFrom
    })
  });
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...VIEWER_TRADE_PROJECTION];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = viewerMarketTradeQueryArgs({
    ...sharedScope,
    from: viewerFrom,
    user: viewerWallet ?? ""
  });
  let t4;
  if ($[1] !== t2 || $[2] !== t3) {
    t4 = {
      fields: t2,
      ...t3
    };
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = viewerWallet !== undefined;
  let t6;
  if ($[4] !== t5) {
    t6 = {
      enabled: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const viewerQuery = useGetTrades(t4, t6);
  const tapeRows = useLiveListData(tapeQuery.queryKey, tapeQuery.rows, tapeQuery.entityQueryKey, tapeQuery.projectEntityRow, subscribeTape);
  const viewerRows = useLiveListData(viewerQuery.queryKey, viewerQuery.rows, viewerQuery.entityQueryKey, viewerQuery.projectEntityRow);
  let t7;
  if ($[6] !== tapeQuery || $[7] !== tapeRows || $[8] !== viewerQuery || $[9] !== viewerRows) {
    t7 = {
      tapeQuery,
      tapeRows,
      viewerQuery,
      viewerRows
    };
    $[6] = tapeQuery;
    $[7] = tapeRows;
    $[8] = viewerQuery;
    $[9] = viewerRows;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}